<template>
  <modal :show="show" :large="true" v-on:close="closeModal">
    <div slot="title">
      <h4>Edit Payment</h4>
    </div>
    <div class="row">
      <div class="col-xl-12">
        <div class="form-horizontal">
          <selectlist
            name="method"
            placeholder="Please Select"
            label="Payment Method"
            v-model="form.method_id"
            :options="methods"
            key-name="id"
          ></selectlist>
          <div class="form-group">
            <label
              for="date"
              class="col-lg-4 control-label"
              style="text-align: left;"
              >Payment Date:</label
            >
            <div class="col-lg-8">
              <datepicker
                v-on:selected="setPaymentDate"
                id="date"
                :value="form.date"
                format="dd/MM/yyyy"
                placeholder="dd/mm/yyyy"
                input-class="form-control"
              ></datepicker>
            </div>
          </div>
          <inputbox
            name="payment_value"
            label="Payment Value"
            type="number"
            v-model="paymentValue"
          >
            <div slot="leftAddon">&#163;</div>
          </inputbox>

          <inputbox
            name="remittance_number"
            label="Remittance Number"
            v-model="form.remittance_number"
          ></inputbox>
        </div>

        <fieldset>
          <legend>
            Rates
            <small
              >- Select the rates you want to allocate to this payment</small
            >
          </legend>
        </fieldset>

        <div class="text-center" :class="{ 'text-danger': leftToAllocate < 0 }">
          <p style="margin-top: 0px;">Amount Left To Allocate</p>
          <h4>{{ leftToAllocate | currency('£', false) }}</h4>
        </div>

        <table class="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Agreed Figure (&pound;)</th>
              <th v-if="vatRegistered">Agreed Figure Inc VAT (&pound;)</th>
              <th>Amount Paid (&pound;)</th>
              <th></th>
              <th>Amount To Allocate</th>
            </tr>
          </thead>
          <tbody
            :key="`rate-groups-${groupIndex}`"
            v-for="(group, groupIndex) in rateGroups"
          >
            <tr class="info">
              <th colspan="2">{{ group.label }}</th>
              <th colspan="4">
                <div class="text-right">
                  <button
                    type="button"
                    class="btn btn-xs btn-info"
                    @click="setFullAndFinal(group)"
                  >
                    Full &amp; Final
                  </button>
                </div>
              </th>
            </tr>

            <tr
              :key="`rate-${rateIndex}`"
              v-for="(rate, rateIndex) in groupRates(group)"
            >
              <td>{{ rate.label }}</td>
              <td>{{ rate.agreed_value | currency('', false) }}</td>
              <td v-if="vatRegistered">
                {{ agreedValueIncludingVat(rate) | currency('') }}
              </td>
              <td>{{ totalPaid(rate) | currency('', false) }}</td>
              <td>
                <toggleswitch
                  :name="`rates[${rate.id}].enabled`"
                  :checked="rateSelected(rate)"
                  @change="toggleRate(rate)"
                ></toggleswitch>
              </td>
              <td>
                <currency-input
                  :name="`rates[${rate.id}].value`"
                  :value="form.rates[rate.id].value"
                  :max="maxRate(rate)"
                  @input="updateRateValue($event, rate)"
                  v-if="rateSelected(rate)"
                ></currency-input>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div slot="footer">
      <div class="float-left">
        <form-button
          level="danger"
          :indicator="deleting"
          v-on:clicked="deletePayment"
        >
          Delete Payment
        </form-button>
      </div>
      <div class="float-right">
        <button type="button" v-on:click="closeModal" class="btn btn-danger">
          Close
        </button>
        <form-button :indicator="form.processing()" v-on:clicked="save">
          Save Payment
        </form-button>
      </div>

      <div class="clearfix"></div>
    </div>
  </modal>
</template>
<script>
import debounce from 'lodash/debounce';
import CurrencyInput from '@/components/Generic/Form/CurrencyInput';

export default {
  props: {
    payment: {
      type: Object,
      required: true
    },
    incident: {
      type: String,
      required: true
    },
    recovery: {
      type: Object,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },

  components: {
    CurrencyInput
  },

  data() {
    return {
      form: new window.Form({
        method_id: null,
        date: null,
        value: 0,
        remittance_number: null,
        rates: {}
      }),
      payments: new window.Form({
        value: 0
      }),
      totalAllocated: 0,
      remittanceNumber: null,
      methods: [],
      deleting: false
    };
  },

  computed: {
    paymentValue: {
      get() {
        return this.penceToPounds(this.form.value);
      },

      set: debounce(function(newValue) {
        if (newValue > 0) {
          this.form.value = parseInt(this.poundsToPence(newValue));
          return;
        }

        this.form.value = 0;
      }, 500)
    },

    leftToAllocate() {
      return this.form.value - this.totalAllocated;
    },

    rateGroups() {
      return this.$store.getters['RateGroups/all'];
    },

    vatRegistered() {
      return this.recovery.include_vat;
    }
  },

  mounted() {
    window.Ajax.get('/payment-methods')
      .then(response => {
        this.methods = response.data;

        this.setDefault();
      })
      .catch(error => {});
  },

  methods: {
    setDefault() {
      this.form.method_id = this.payment.method_id;
      this.form.date = window.DateManager.format(this.payment.date);
      this.form.value = this.payment.value;
      this.form.remittance_number = this.payment.remittance_number;

      this.payment.rates.forEach(
        rate =>
          (this.form.rates[rate.rate_id] = {
            rate_id: rate.rate_id,
            value: rate.value
          })
      );

      this.calculateTotalAllocated();
    },

    closeModal() {
      this.$emit('close');
    },

    setPaymentDate(value) {
      this.form.date = value;
    },

    save() {
      this.form
        .put(
          `/debt-recovery/${this.incident}/payments/${this.payment.identifier}`
        )
        .then(response => {
          window.toastify.success(
            'Success',
            'The payment was updated successfully!'
          );
          this.closeModal();
        })
        .catch(error => {
          window.toastify.error(
            'Sorry, there was a problem saving the payment'
          );
        });
    },

    groupRates(group) {
      return this.recovery?.rates?.filter(
        rate => rate.rate_group_id === group.id
      );
    },

    rateSelected(rate) {
      return !!this.form.rates[rate.id];
    },

    toggleRate(rate) {
      if (this.rateSelected(rate)) {
        delete this.form.rates[rate.id];

        this.calculateTotalAllocated();
      } else {
        this.form.rates[rate.id] = {
          rate_id: rate.id,
          value: 0
        };
      }

      this.$forceUpdate();
    },

    updateRateValue(value, rate) {
      this.form.rates[rate.id].value = value;

      this.calculateTotalAllocated();
    },

    calculateTotalAllocated() {
      this.totalAllocated = Object.values(this.form.rates).reduce(
        (total, rate) => {
          return total + rate.value;
        },
        0
      );
    },

    totalPaid(rate) {
      const paid = this.payment.rates.find(r => r.rate_id === rate.id);

      if (paid) {
        return rate.total_paid - paid.value;
      }

      return rate.total_paid;
    },

    maxRate(rate) {
      if (!this.vatRegistered) {
        return rate.agreed_value - this.totalPaid(rate);
      }

      return this.agreedValueIncludingVat(rate) - this.totalPaid(rate);
    },

    agreedValueIncludingVat(rate) {
      if (rate.agreed_value) {
        return rate.agreed_value * 1.2;
      }

      return null;
    },

    setFullAndFinal(group) {
      this.groupRates(group).forEach(rate => {
        if (this.maxRate(rate) > 0) {
          if (!this.rateSelected(rate)) {
            this.toggleRate(rate);
          }

          this.updateRateValue(this.maxRate(rate), rate);
        }
      });

      window.toastify.success('Updated', 'The full & final values were set!');
    },

    deletePayment() {
      window
        .swal({
          title: 'Are you sure?',
          text: 'This will permanently remove the payment',
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          this.deleting = true;

          window.Ajax.delete(
            `/debt-recovery/${this.incident}/payments/${this.payment.identifier}`
          )
            .then(() => {
              window.toastify.success(
                'Success',
                'The payment was deleted successfully!'
              );
              this.closeModal();
            })
            .catch(() => {
              window.toastify.error(
                'Sorry, there was a problem deleting the payment'
              );
            });
        });
    }
  }
};
</script>
